import React, { useEffect, useMemo, useRef, useState } from "react";
import { adminApi, absoluteAdminUrl } from "./api";
import { formatINR, parseNumber, shortDate } from "./format";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, X, Package, ImagePlus, Sparkles, Loader2 } from "lucide-react";
import ExportMenu from "./ExportMenu";

const EMPTY = {
  name: "",
  category: "Lighting",
  brand: "",
  sku: "",
  description: "",
  unit: "Nos",
  price: 0,
  image_url: "",
  stock: 0,
};

const CATEGORIES = ["Lighting", "Curtains", "Climate", "Security", "Entertainment", "Sensors", "Switches", "Other"];

export default function InventoryModule() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/products");
      setProducts(r.data?.products || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return products.filter((p) => {
      if (catFilter !== "all" && p.category !== catFilter) return false;
      if (!term) return true;
      return (
        (p.name || "").toLowerCase().includes(term) ||
        (p.brand || "").toLowerCase().includes(term) ||
        (p.sku || "").toLowerCase().includes(term)
      );
    });
  }, [products, q, catFilter]);

  const onSave = async (data, existing) => {
    try {
      if (existing) {
        await adminApi.put(`/products/${existing.id}`, data);
        toast.success("Product updated");
      } else {
        await adminApi.post("/products", data);
        toast.success("Product added");
      }
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await adminApi.delete(`/products/${p.id}`);
      toast.success("Product deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="p-10 max-w-[1320px] mx-auto" data-testid="inventory-module">
      <header className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Package className="w-3.5 h-3.5" style={{ color: "var(--ax-gold)" }} />
            <span className="text-[10.5px] tracking-[0.32em] uppercase" style={{ color: "var(--ax-gold)" }}>
              Inventory
            </span>
          </div>
          <h1 className="ax-serif text-5xl tracking-[-0.02em]">Catalog</h1>
          <p className="text-sm mt-3" style={{ color: "var(--ax-text-dim)" }}>
            Curate the GenieX library — AI writes the &ldquo;what it does&rdquo; line for every product, automatically.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ExportMenu entity="products" testidPrefix="inv-export" />
          <button type="button" className="ax-btn ax-btn-gold" onClick={() => setEditing("new")} data-testid="inv-add-product">
            <Plus className="w-4 h-4" /> Add product
          </button>
        </div>
      </header>

      <div className="ax-glass mb-5 p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg flex-1 min-w-[240px]" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid var(--ax-border)" }}>
          <Search className="w-4 h-4" style={{ color: "var(--ax-text-muted)" }} />
          <input
            className="bg-transparent flex-1 outline-none text-sm"
            placeholder="Search by name, brand, SKU"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="inv-search"
          />
        </div>
        <select className="ax-select" style={{ width: 180 }} value={catFilter} onChange={(e) => setCatFilter(e.target.value)} data-testid="inv-filter-cat">
          <option value="all">All categories</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <div className="text-xs" style={{ color: "var(--ax-text-muted)" }}>
          {filtered.length} / {products.length}
        </div>
      </div>

      <div className="ax-glass overflow-hidden" data-testid="inv-table-wrap">
        <div className="ax-scroll overflow-x-auto">
          <table className="ax-table">
            <thead>
              <tr>
                <th style={{ width: 64 }}></th>
                <th>Product</th>
                <th>Category</th>
                <th>SKU</th>
                <th>Unit</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Updated</th>
                <th style={{ width: 110 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={9} className="text-center py-10 text-sm" style={{ color: "var(--ax-text-muted)" }}>
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-10 text-sm" style={{ color: "var(--ax-text-muted)" }}>
                    No products match.
                  </td>
                </tr>
              )}
              {!loading &&
                filtered.map((p) => (
                  <tr key={p.id} data-testid={`inv-row-${p.id}`}>
                    <td>
                      {p.image_url ? (
                        <img
                          src={absoluteAdminUrl(p.image_url)}
                          alt=""
                          className="w-12 h-12 rounded-md object-cover"
                          style={{ border: "1px solid var(--ax-border)" }}
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-md flex items-center justify-center" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid var(--ax-border)" }}>
                          <Package className="w-4 h-4" style={{ color: "var(--ax-text-muted)" }} />
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="text-sm font-medium">{p.name}</div>
                      {p.description && (
                        <div className="text-[11px] mt-0.5 line-clamp-2 max-w-[360px]" style={{ color: "var(--ax-text-muted)" }}>
                          {p.description}
                        </div>
                      )}
                      {p.brand && <div className="text-[11px]" style={{ color: "var(--ax-text-dim)" }}>{p.brand}</div>}
                    </td>
                    <td><span className="ax-chip">{p.category}</span></td>
                    <td className="text-xs ax-mono">{p.sku || "—"}</td>
                    <td className="text-xs">{p.unit || "Nos"}</td>
                    <td className="text-sm ax-mono" style={{ color: "var(--ax-gold)" }}>{formatINR(p.price)}</td>
                    <td className="text-sm">{p.stock ?? 0}</td>
                    <td className="text-xs" style={{ color: "var(--ax-text-muted)" }}>{shortDate(p.updated_at)}</td>
                    <td>
                      <div className="flex items-center gap-1 justify-end">
                        <button type="button" className="ax-btn ax-btn-ghost px-2" onClick={() => setEditing(p)} data-testid={`inv-edit-${p.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button type="button" className="ax-btn ax-btn-ghost ax-btn-danger px-2" onClick={() => onDelete(p)} data-testid={`inv-delete-${p.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <ProductDialog
          product={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={onSave}
        />
      )}
    </div>
  );
}

function ProductDialog({ product, onClose, onSave }) {
  const [form, setForm] = useState(() => ({ ...EMPTY, ...(product || {}) }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const fileRef = useRef(null);
  const aiAutoFiredRef = useRef(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const triggerAI = async () => {
    if (!form.name.trim()) return;
    setAiBusy(true);
    try {
      const r = await adminApi.post("/ai/describe-product", {
        name: form.name,
        category: form.category,
        brand: form.brand,
      });
      const text = r.data?.description || "";
      if (text) set("description", text);
    } catch (e) {
      // silent fallback handled server-side; show toast only if no description filled
      if (!form.description) toast.error("AI description unavailable, you can edit manually");
    } finally {
      setAiBusy(false);
    }
  };

  // Auto-generate "What it does" the first time a NEW product has a name and no description.
  useEffect(() => {
    if (product) return; // edit mode: don't auto-fill
    if (aiAutoFiredRef.current) return;
    if (!form.name.trim() || form.description.trim()) return;
    const t = setTimeout(() => {
      aiAutoFiredRef.current = true;
      triggerAI();
    }, 600);
    return () => clearTimeout(t);
  }, [form.name, form.category, form.brand]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await adminApi.post("/products/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("image_url", r.data?.image_url || "");
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    let payload = {
      ...form,
      price: parseNumber(form.price),
      stock: parseInt(parseNumber(form.stock), 10) || 0,
    };

    // If still no description on a brand-new product, request AI one final time inline
    if (!product && !payload.description.trim()) {
      try {
        const r = await adminApi.post("/ai/describe-product", {
          name: payload.name,
          category: payload.category,
          brand: payload.brand,
        });
        payload.description = r.data?.description || "";
      } catch {
        /* silent fallback */
      }
    }
    await onSave(payload, product);
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 ax-scroll overflow-y-auto"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}
      data-testid="inv-dialog"
    >
      <form onSubmit={submit} className="ax-glass w-full max-w-3xl p-7 relative my-10" style={{ borderRadius: 18 }}>
        <button type="button" className="absolute top-4 right-4 text-white/60 hover:text-white" onClick={onClose} data-testid="inv-dialog-close">
          <X className="w-4 h-4" />
        </button>
        <div className="mb-5">
          <div className="text-[10.5px] tracking-[0.32em] uppercase" style={{ color: "var(--ax-gold)" }}>
            {product ? "Edit" : "New"} product
          </div>
          <h2 className="ax-serif text-3xl mt-1">{product ? product.name : "Add to catalog"}</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="md:col-span-1">
            <label className="ax-label">Image</label>
            <div
              className="rounded-xl flex items-center justify-center cursor-pointer overflow-hidden relative"
              style={{
                height: 200,
                background: "rgba(255,255,255,0.02)",
                border: "1px dashed var(--ax-border)",
              }}
              onClick={() => fileRef.current?.click()}
              data-testid="inv-image-drop"
            >
              {form.image_url ? (
                <img src={absoluteAdminUrl(form.image_url)} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center" style={{ color: "var(--ax-text-muted)" }}>
                  <ImagePlus className="w-6 h-6 mx-auto mb-2" />
                  <div className="text-xs">Click to upload</div>
                  <div className="text-[10px] mt-1">PNG / JPG / WEBP · up to 5 MB</div>
                </div>
              )}
              {uploading && (
                <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.6)" }}>
                  <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--ax-gold)" }} />
                </div>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => handleUpload(e.target.files?.[0])}
              data-testid="inv-image-input"
            />
          </div>

          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="ax-label">Product name *</label>
              <input className="ax-input" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="inv-field-name" />
            </div>
            <div>
              <label className="ax-label">Category</label>
              <select className="ax-select" value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="inv-field-category">
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="ax-label">Brand</label>
              <input className="ax-input" value={form.brand || ""} onChange={(e) => set("brand", e.target.value)} data-testid="inv-field-brand" />
            </div>
            <div>
              <label className="ax-label">SKU</label>
              <input className="ax-input" value={form.sku || ""} onChange={(e) => set("sku", e.target.value)} data-testid="inv-field-sku" />
            </div>
            <div>
              <label className="ax-label">Unit</label>
              <select className="ax-select" value={form.unit || "Nos"} onChange={(e) => set("unit", e.target.value)} data-testid="inv-field-unit">
                <option>Nos</option>
                <option>Set</option>
                <option>Pair</option>
                <option>Meter</option>
                <option>Sqft</option>
              </select>
            </div>
            <div>
              <label className="ax-label">Price (₹)</label>
              <input className="ax-input ax-mono" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="inv-field-price" />
            </div>
            <div>
              <label className="ax-label">Stock</label>
              <input className="ax-input ax-mono" value={form.stock ?? 0} onChange={(e) => set("stock", e.target.value)} data-testid="inv-field-stock" />
            </div>
            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-1.5">
                <span className="ax-label !mb-0">What it does (AI)</span>
                <button
                  type="button"
                  className="ax-btn ax-btn-ghost px-2 py-1 text-[11px]"
                  onClick={triggerAI}
                  disabled={aiBusy || !form.name.trim()}
                  data-testid="inv-ai-regenerate"
                >
                  {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" style={{ color: "var(--ax-gold)" }} />}
                  {aiBusy ? "Generating…" : "Regenerate"}
                </button>
              </div>
              <textarea
                className="ax-textarea"
                rows={3}
                placeholder={aiBusy ? "AI is writing the product line…" : "Auto-filled when you type the product name. You can edit."}
                value={form.description || ""}
                onChange={(e) => set("description", e.target.value)}
                data-testid="inv-field-description"
              />
            </div>
          </div>
        </div>

        <div className="mt-7 flex items-center justify-end gap-3">
          <button type="button" className="ax-btn ax-btn-ghost" onClick={onClose} data-testid="inv-dialog-cancel">
            Cancel
          </button>
          <button type="submit" className="ax-btn ax-btn-gold" disabled={saving} data-testid="inv-dialog-save">
            {saving ? "Saving…" : product ? "Save changes" : "Add product"}
          </button>
        </div>
      </form>
    </div>
  );
}
